import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CheckCircle, Clock, Briefcase, Eye, Search, Package, ChevronDown, LogOut, CheckCircle2 } from 'lucide-react';

// Import Artie logo
import artieLogo from "@/assets/images/artie.png";

interface Campaign {
  id: string;
  name: string;
  description: string;
  budget: number;
  status: string;
  window_details?: {
    start_date: string;
    end_date: string;
  };
  requirements: {
    tone_preferences: string[];
    target_categories: string[];
    suitability_filters: string[];
    preferred_verticals: string[];
    budget_usd?: number;
  };
}

interface Package {
  id: string;
  name: string;
  description: string;
  status: string;
  campaign_id?: string;
  total_reach: number;
  total_cost?: number;
  total_budget?: number;
  placement_count?: number;
  project_count?: number;
  estimated_cpm?: number;
  creator_approval_deadline?: string;
}

export default function HoldPlacedPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { user, brand, logout } = useAuthStore();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);

  // Update current time every minute for countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isProfileDropdownOpen) {
        const target = event.target as HTMLElement;
        if (!target.closest('.profile-dropdown')) {
          setIsProfileDropdownOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileDropdownOpen]);

  // Fetch campaign data
  const { data: campaign, isLoading: campaignLoading } = useQuery<Campaign>({
    queryKey: ['campaign', campaignId],
    queryFn: () => apiClient.getCampaign(campaignId!),
    enabled: !!campaignId,
  });

  // Fetch packages data to get the package that has the hold placed
  const { data: packagesData, isLoading: packagesLoading } = useQuery({
    queryKey: ['packages', campaignId],
    queryFn: async () => {
      // Use campaign_id query parameter to filter packages on the backend
      const data = await apiClient.get<{ packages: any[] }>(`/api/v1/ops-admin/packages?campaign_id=${campaignId}`);
      
      // Return the first package for this campaign (should only be one with the filter)
      return data.packages?.[0] || null;
    },
    enabled: !!campaignId,
  });

  // Calculate package metrics from the raw data
  const calculatePackageMetrics = (pkg: any) => {
    if (!pkg) return { placement_count: 0, project_count: 0, estimated_cpm_range: null };
    
    // Calculate placement count from anchor and fill projects
    const anchorProjects = pkg.anchor_projects || pkg.anchor_shows || [];
    const fillProjects = pkg.fill_projects || pkg.fill_shows || [];
    
    const anchorPlacements = anchorProjects.reduce((sum: number, project: any) =>
      sum + ((project.slots?.length || 0) + (project.available_slots?.length || 0) + (project.assigned_slots?.length || 0)), 0);
    const fillPlacements = fillProjects.reduce((sum: number, project: any) =>
      sum + ((project.slots?.length || 0) + (project.available_slots?.length || 0) + (project.assigned_slots?.length || 0)), 0);
    
    const placement_count = anchorPlacements + fillPlacements;
    const project_count = anchorProjects.length + fillProjects.length;
    
    // Use target eCPM range from package targeting data
    const targetEcpmRange = pkg.targeting?.target_ecpm_range;
    let estimated_cpm_range = null;
    
    if (targetEcpmRange && targetEcpmRange.min !== undefined && targetEcpmRange.max !== undefined) {
      estimated_cpm_range = {
        min: targetEcpmRange.min,
        max: targetEcpmRange.max
      };
    } else {
      // Fallback to calculated CPM if no target range is available
      const budget = pkg.total_budget || pkg.total_cost || 0;
      const reach = pkg.total_reach || 0;
      const calculated_cpm = reach > 0 ? (budget / reach) * 1000 : 0;
      estimated_cpm_range = {
        min: calculated_cpm,
        max: calculated_cpm
      };
    }
    
    return { placement_count, project_count, estimated_cpm_range };
  };

  // Use the fetched package data or fallback to default data
  const packageMetrics = calculatePackageMetrics(packagesData);
  const packageData: Package = packagesData ? {
    id: packagesData.id || 'pkg-1',
    name: packagesData.name || 'Package',
    description: packagesData.description || 'Package description',
    status: 'hold_placed',
    total_reach: packagesData.total_reach || 0,
    total_cost: packagesData.total_cost || packagesData.total_budget || 0,
    total_budget: packagesData.total_budget || packagesData.total_cost || 0,
    placement_count: packageMetrics.placement_count,
    project_count: packageMetrics.project_count,
    estimated_cpm: packageMetrics.estimated_cpm_range ? (packageMetrics.estimated_cpm_range.min + packageMetrics.estimated_cpm_range.max) / 2 : 0,
    creator_approval_deadline: packagesData.creator_approval_deadline || new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString()
  } : {
    id: 'pkg-1',
    name: 'Package',
    description: 'Package description',
    status: 'hold_placed',
    total_reach: 0,
    total_cost: 0,
    total_budget: 0,
    placement_count: 0,
    project_count: 0,
    estimated_cpm: 0,
    creator_approval_deadline: new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString()
  };

  // Get the eCPM range for display
  const getEstimatedCpmDisplay = () => {
    const range = packageMetrics.estimated_cpm_range;
    if (range && range.min !== undefined && range.max !== undefined) {
      if (range.min === range.max) {
        return `$${range.min.toFixed(2)}`;
      } else {
        return `$${range.min.toFixed(2)} - $${range.max.toFixed(2)}`;
      }
    }
    return '$0.00';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  // Calculate countdown to creator approval deadline
  const getCountdown = () => {
    if (!packageData.creator_approval_deadline) return null;

    const deadline = new Date(packageData.creator_approval_deadline);
    const now = currentTime;
    const timeDiff = deadline.getTime() - now.getTime();

    if (timeDiff <= 0) {
      return { expired: true };
    }

    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    return {
      expired: false,
      days,
      hours,
      formattedDeadline: deadline.toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short'
      })
    };
  };

  const countdown = getCountdown();

  if (campaignLoading || packagesLoading) {
    return (
      <div className="min-h-screen bg-white font-geist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900 mx-auto"></div>
            <p className="mt-4 text-neutral-600">Loading campaign...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-white font-geist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-neutral-900 mb-2">Campaign not found</h2>
            <p className="text-neutral-600 mb-4">The campaign you're looking for doesn't exist or you don't have access to it.</p>
            <Button onClick={() => window.location.href = '/brand-dashboard'}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-geist">
      {/* Top Navigation */}
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left side - Logo */}
            <Link to="/brand-dashboard" className="flex items-center hover:opacity-80 transition-opacity">
              <img src={artieLogo} alt="Artie" className="w-10 h-10 mr-3" />
              <span className="font-work font-semibold text-3xl text-neutral-900 tracking-normal">artie</span>
            </Link>
            
            {/* Center - Navigation */}
            <nav className="hidden md:flex space-x-8">
              <Link to="/brand-dashboard" className="text-neutral-600 hover:text-neutral-900 font-medium">
                Home
              </Link>
              <Link to="/campaigns" className="text-neutral-900 font-semibold">
                Campaigns
              </Link>
              <span className="text-neutral-600 hover:text-neutral-900 font-medium cursor-pointer">IO & Payments</span>
              <span className="text-neutral-600 hover:text-neutral-900 font-medium cursor-pointer">Settings</span>
            </nav>
            
            {/* Right side - User info */}
            <div className="flex items-center space-x-4">
              <div className="relative profile-dropdown">
                <button
                  onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                  className="flex items-center space-x-2 text-sm text-neutral-600 hover:text-neutral-900 transition-colors"
                >
                  <span className="bg-neutral-100 px-3 py-1 rounded-full font-medium">
                    {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isProfileDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Dropdown Menu */}
                {isProfileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-neutral-200 rounded-lg shadow-lg z-50">
                    <div className="py-2">
                      <div className="px-4 py-2 border-b border-neutral-100">
                        <p className="text-sm font-medium text-neutral-900">{user?.name || 'User'}</p>
                        <p className="text-xs text-neutral-500">{user?.email || 'user@example.com'}</p>
                      </div>
                      <div className="pt-2">
                        <button
                          onClick={() => {
                            setIsProfileDropdownOpen(false);
                            logout();
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <LogOut className="w-4 h-4 mr-3" />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Link */}
        <Link
          to="/campaigns"
          className="inline-flex items-center text-sm text-neutral-500 hover:text-neutral-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          ALL CAMPAIGNS
        </Link>

        {/* Campaign Title */}
        <h1 className="text-4xl font-bold text-neutral-900 mb-12">{campaign.name}</h1>

        {/* Campaign Details Section */}
        <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-8 mb-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Total Budget */}
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wider mb-4">TOTAL BUDGET</h3>
              <div className="text-4xl font-bold text-neutral-900">
                {formatCurrency(campaign.requirements?.budget_usd || campaign.budget || 0)}
              </div>
            </div>

            {/* Tone */}
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wider mb-4">TONE</h3>
              <div className="flex flex-wrap gap-2">
                {campaign.requirements?.suitability_filters?.length > 0 ? (
                  campaign.requirements.suitability_filters.map((filter: string, index: number) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-neutral-100 text-neutral-700 rounded-md text-sm font-medium"
                    >
                      {filter.charAt(0).toUpperCase() + filter.slice(1)}
                    </span>
                  ))
                ) : (
                  <span className="text-neutral-500 text-sm italic">No tone filters specified</span>
                )}
              </div>
            </div>

            {/* Content Vertical */}
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wider mb-4">CONTENT VERTICAL</h3>
              <div className="flex flex-wrap gap-2">
                {campaign.requirements?.preferred_verticals?.length > 0 ? (
                  campaign.requirements.preferred_verticals.map((vertical: string, index: number) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-neutral-100 text-neutral-700 rounded-md text-sm font-medium"
                    >
                      {vertical.charAt(0).toUpperCase() + vertical.slice(1)}
                    </span>
                  ))
                ) : (
                  <span className="text-neutral-500 text-sm italic">No content verticals specified</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Campaign Progress */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-neutral-900 mb-12 text-center">Campaign Progress</h2>
          
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            {/* Submit Requirements */}
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-neutral-800 rounded-full flex items-center justify-center mb-3">
                <CheckCircle2 className="w-6 h-6 text-white" />
              </div>
              <div className="text-center">
                <div className="font-semibold text-neutral-900 text-sm">Submit</div>
                <div className="font-semibold text-neutral-900 text-sm">Requirements</div>
                <div className="text-xs text-neutral-500 mt-1">Completed</div>
              </div>
            </div>

            {/* Progress Line */}
            <div className="flex-1 h-0.5 bg-neutral-800 mx-4"></div>

            {/* Browse Eligible Projects */}
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-neutral-800 rounded-full flex items-center justify-center mb-3">
                <Search className="w-6 h-6 text-white" />
              </div>
              <div className="text-center">
                <div className="font-semibold text-neutral-900 text-sm">Browse Eligible</div>
                <div className="font-semibold text-neutral-900 text-sm">Projects</div>
                <div className="text-xs text-neutral-500 mt-1">Completed</div>
              </div>
            </div>

            {/* Progress Line */}
            <div className="flex-1 h-0.5 bg-neutral-800 mx-4"></div>

            {/* Packages Ready */}
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-neutral-800 rounded-full flex items-center justify-center mb-3">
                <Package className="w-6 h-6 text-white" />
              </div>
              <div className="text-center">
                <div className="font-semibold text-neutral-900 text-sm">Packages</div>
                <div className="font-semibold text-neutral-900 text-sm">Ready</div>
                <div className="text-xs text-neutral-500 mt-1">Completed</div>
              </div>
            </div>

            {/* Progress Line */}
            <div className="flex-1 h-0.5 bg-neutral-800 mx-4"></div>

            {/* Select & Confirm - Completed */}
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-neutral-800 rounded-full flex items-center justify-center mb-3">
                <CheckCircle2 className="w-6 h-6 text-white" />
              </div>
              <div className="text-center">
                <div className="font-semibold text-neutral-900 text-sm">Select &</div>
                <div className="font-semibold text-neutral-900 text-sm">Confirm</div>
                <div className="text-xs text-orange-600 mt-1">Awaiting Creator</div>
                <div className="text-xs text-orange-600">Approvals</div>
              </div>
            </div>

            {/* Progress Line */}
            <div className="flex-1 h-0.5 bg-neutral-300 mx-4"></div>

            {/* Campaign Live - Pending */}
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-neutral-300 rounded-full flex items-center justify-center mb-3">
                <CheckCircle2 className="w-6 h-6 text-neutral-500" />
              </div>
              <div className="text-center">
                <div className="font-semibold text-neutral-500 text-sm">Campaign</div>
                <div className="font-semibold text-neutral-500 text-sm">Live</div>
                <div className="text-xs text-neutral-400 mt-1">Pending</div>
              </div>
            </div>
          </div>
        </div>

        {/* Hold Placed Status */}
        <div className="bg-white border border-neutral-200 rounded-xl p-8 mb-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-neutral-900 text-white rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-bold text-neutral-900 mb-2">Hold Placed — Awaiting Creator Approvals</h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              Your hold on the <strong>{packageData.name}</strong> package has been placed. Creators are reviewing your brand. We'll return with confirmed placements within 48 hours.
            </p>
          </div>

          {/* Package Stats */}
          <div className="grid grid-cols-2 gap-8 mb-6">
            <div>
              <p className="text-sm font-medium text-neutral-600 uppercase tracking-wider mb-2">PACKAGE</p>
              <p className="text-xl font-semibold text-neutral-900">{packageData.name}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-neutral-600 uppercase tracking-wider mb-2">BUDGET</p>
              <p className="text-xl font-semibold text-neutral-900">{formatCurrency(packageData.total_budget || 0)}</p>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4 mb-6">
            <div>
              <p className="text-sm font-medium text-neutral-600 uppercase tracking-wider mb-2">ESTIMATED CPM</p>
              <p className="text-xl font-semibold text-neutral-900">{getEstimatedCpmDisplay()}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-neutral-600 uppercase tracking-wider mb-2">ESTIMATED VIEWS</p>
              <p className="text-xl font-semibold text-neutral-900">{formatNumber(packageData.total_reach || 0)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-neutral-600 uppercase tracking-wider mb-2">PLACEMENTS</p>
              <p className="text-xl font-semibold text-neutral-900">{packageData.placement_count || 0}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-neutral-600 uppercase tracking-wider mb-2">PROJECTS</p>
              <p className="text-xl font-semibold text-neutral-900">{packageData.project_count || 0}</p>
            </div>
          </div>

          {/* Countdown */}
          {countdown && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex justify-between items-center text-sm text-yellow-800">
                <span>Creator Confirmation Window Closes In</span>
                {countdown.expired ? (
                  <span className="text-red-600 font-semibold">Expired</span>
                ) : (
                  <span>
                    <span className="font-semibold">
                      {countdown.days > 0 && `${countdown.days} day${countdown.days !== 1 ? 's' : ''} `}
                      {countdown.hours} hour{countdown.hours !== 1 ? 's' : ''}
                    </span>{' '}
                    {countdown.formattedDeadline}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Contact Section */}
        <div className="text-center">
          <p className="text-neutral-600 mb-2">Have a question?</p>
          <Button
            variant="link"
            className="text-blue-600 hover:text-blue-800 p-0 transition-colors duration-200"
            onClick={() => {
              // Open email client with pre-filled subject and body
              const subject = encodeURIComponent(`Question about Hold Placed Campaign: ${campaign?.name || 'Campaign'}`);
              const body = encodeURIComponent(`Hi Artie team,\n\nI have a question about my campaign with a hold placed:\n\nCampaign: ${campaign?.name || 'N/A'}\nCampaign ID: ${campaignId || 'N/A'}\n\nQuestion:\n\n\nBest regards`);
              window.location.href = `mailto:support@artie.com?subject=${subject}&body=${body}`;
            }}
          >
            Send us a message
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-neutral-200 py-6 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <p className="text-sm text-neutral-500">© 2026 Artie. All rights reserved.</p>
            <div className="flex space-x-4">
              <a href="#" className="text-neutral-400 hover:text-neutral-600">
                <span className="sr-only">LinkedIn</span>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clipRule="evenodd" />
                </svg>
              </a>
              <a href="#" className="text-neutral-400 hover:text-neutral-600">
                <span className="sr-only">Twitter</span>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}